import React, { useEffect, useCallback, useRef } from "react";
import { LevelProps, ItemRenderProps, Path } from "../types";
import {
    useCommandDropdown,
    useLevelContext,
    LevelContext,
    matchesSearch,
    pathsEqual,
    pathToKey,
} from "../context";
import { List, ListItemButton } from "@mui/material";
import { ChevronRight, Check } from "lucide-react";

// A row rendered by the level: either one of its own options, or (while searching with
// `getChildren`) a matching descendant together with the items leading to it.
interface LevelEntry<T> {
    item: T;
    key: string | number;
    label: React.ReactNode;
    labelStr: string;
    /** Keys from this level down to the item (a single key for the level's own options) */
    relativePath: Path;
    /** Items between this level and the item (empty for the level's own options) */
    ancestors: Array<{ key: string | number; label: string; item: T }>;
}

export function Level<T>({
    options,
    getKey,
    getLabel,
    getCode,
    getSearchKeywords,
    hasChildren: hasChildrenFn,
    allowParentSelect = false,
    getChildren,
    renderItem,
    children,
    onSelect,
}: LevelProps<T>) {
    const {
        searchValue,
        filterMode,
        selections,
        onItemSelect,
        registerItems,
        navigationStack,
        pushNavigation,
        activeIndex,
        setActiveIndex,
        registerItemAction,
        clearItemActions,
        registerLabel,
        updateNavigationLabel,
        registerPathForId,
        registerItemForPath,
    } = useCommandDropdown();

    const { depth, parentPath } = useLevelContext();

    // Level
    // - Renders the current hierarchy level based on navigation stack depth.
    // - Registers labels/paths/items to the shared registry so selection/labels work globally.
    // - Handles row clicks (select vs drill) and keyboard action registration.

    // Refs for scrolling active item into view
    const itemRefs = useRef<Map<number, HTMLElement>>(new Map());

    // Determine if this level should be visible
    // Level is visible if its depth matches the navigation stack length
    // depth 0 = root, visible when stack is empty
    // depth 1 = first child level, visible when stack has 1 item
    const isCurrentLevel = depth === navigationStack.length;

    // Get the options for this level
    // If we're at depth > 0, we need to get options from the navigation stack's item
    const levelOptions = React.useMemo(() => {
        if (depth === 0) {
            return options;
        }
        // For nested levels, options are passed via the children render prop
        // The parent level already filters which item's children to show
        return options;
    }, [options, depth]);

    // Register labels for all options in this level (path -> label)
    useEffect(() => {
        if (!levelOptions) return;
        levelOptions.forEach((item) => {
            const key = getKey(item);
            const label = getLabel(item);
            const labelStr = typeof label === "string" ? label : String(key);
            const itemPath = [...parentPath, key];
            registerLabel(itemPath, labelStr);
            registerPathForId(key, itemPath);
            registerItemForPath(itemPath, item);
        });
    }, [
        levelOptions,
        getKey,
        getLabel,
        registerLabel,
        registerPathForId,
        registerItemForPath,
        parentPath,
    ]);

    // Update navigation label for the item we drilled into (breadcrumbs)
    useEffect(() => {
        if (!levelOptions || depth >= navigationStack.length) return;
        const navigatedItem = navigationStack[depth];
        const matchedItem = levelOptions.find((item) => getKey(item) === navigatedItem.key);
        if (!matchedItem) return;
        const label = getLabel(matchedItem);
        const labelStr = typeof label === "string" ? label : String(navigatedItem.key);
        updateNavigationLabel([...parentPath, navigatedItem.key], labelStr);
    }, [levelOptions, navigationStack, depth, getKey, getLabel, updateNavigationLabel, parentPath]);

    const matchesItem = useCallback(
        (item: T) => {
            const label = getLabel(item);
            const code = getCode?.(item);
            const labelKeyword = typeof label === "string" ? label : undefined;
            const codeKeyword =
                code !== undefined && code !== null && code !== "" ? String(code) : undefined;
            const extraKeywords = getSearchKeywords?.(item) ?? [];
            const keywords = [labelKeyword, codeKeyword, ...extraKeywords].filter(
                (kw): kw is string => !!kw
            );
            return matchesSearch(searchValue, label, keywords);
        },
        [searchValue, getLabel, getCode, getSearchKeywords]
    );

    // Rows of this level: its own options, filtered by search according to the filter mode.
    // With `all-levels` and `getChildren`, a search also walks each option's subtree (depth-first),
    // so matching descendants are listed right after the items they belong to.
    const entries = React.useMemo<LevelEntry<T>[]>(() => {
        if (!levelOptions) return [];

        const toEntry = (item: T, ancestors: LevelEntry<T>["ancestors"]): LevelEntry<T> => {
            const key = getKey(item);
            const label = getLabel(item);
            return {
                item,
                key,
                label,
                labelStr: typeof label === "string" ? label : String(key),
                relativePath: [...ancestors.map((ancestor) => ancestor.key), key],
                ancestors,
            };
        };

        const isSearching = filterMode !== "none" && !!searchValue.trim();

        if (!isSearching) {
            return levelOptions.map((item) => toEntry(item, []));
        }

        const searchesDescendants = filterMode === "all-levels" && !!getChildren;
        const result: LevelEntry<T>[] = [];

        const walk = (items: T[], ancestors: LevelEntry<T>["ancestors"]) => {
            items.forEach((item) => {
                const entry = toEntry(item, ancestors);

                if (matchesItem(item)) {
                    result.push(entry);
                }

                if (searchesDescendants) {
                    const nested = getChildren(item);
                    if (nested.length) {
                        walk(nested, [
                            ...ancestors,
                            { key: entry.key, label: entry.labelStr, item },
                        ]);
                    }
                }
            });
        };

        walk(levelOptions, []);

        return result;
    }, [levelOptions, searchValue, filterMode, getKey, getLabel, getChildren, matchesItem]);

    // Register item count for Empty component visibility (only for current visible level).
    useEffect(() => {
        if (isCurrentLevel) {
            registerItems(entries.length);
        }
    }, [entries.length, isCurrentLevel, registerItems]);

    // Rebuilt `entries` arrays (e.g. from inline getters) shouldn't reset the highlighted row,
    // only an actual change of the listed rows should
    const entriesSignature = React.useMemo(
        () => entries.map((entry) => pathToKey(entry.relativePath)).join("|"),
        [entries]
    );

    // Reset active index when options change or when becoming current level
    useEffect(() => {
        if (isCurrentLevel) {
            setActiveIndex(0);
        }
    }, [entriesSignature, isCurrentLevel, setActiveIndex]);

    // Opens the entry's children. Nested search results sit below other items,
    // so the whole chain leading to them is pushed as well.
    const drillInto = useCallback(
        (entry: LevelEntry<T>) => {
            entry.ancestors.forEach((ancestor) =>
                pushNavigation(ancestor.key, ancestor.label, ancestor.item)
            );
            pushNavigation(entry.key, entry.labelStr, entry.item);
        },
        [pushNavigation]
    );

    // Handle item click/action.
    // If parent selection is not allowed, clicks drill down; otherwise they select.
    const handleItemAction = useCallback(
        (entry: LevelEntry<T>) => {
            const itemPath: Path = [...parentPath, ...entry.relativePath];

            // Check if this item has children - ONLY use hasChildrenFn, not the existence of children prop
            const itemHasChildren = hasChildrenFn?.(entry.item) ?? false;
            const canNavigate = itemHasChildren && children;

            if (canNavigate && !allowParentSelect) {
                // When parent selection is not allowed, row clicks drill down.
                drillInto(entry);
                return;
            }

            let labels: string[] | undefined;

            if (entry.ancestors.length) {
                // Nested results are selected from above their own level, so their ancestors
                // are registered here and passed explicitly instead of coming from the navigation stack
                entry.ancestors.forEach((ancestor, index) => {
                    const ancestorPath = [...parentPath, ...entry.relativePath.slice(0, index + 1)];
                    registerLabel(ancestorPath, ancestor.label);
                    registerItemForPath(ancestorPath, ancestor.item);
                });
                registerItemForPath(itemPath, entry.item);

                labels = [
                    ...navigationStack.map((nav) => nav.label),
                    ...entry.ancestors.map((ancestor) => ancestor.label),
                    entry.labelStr,
                ];
            }

            // When parent selection is allowed (or this is a leaf), row clicks select the item.
            onSelect?.(entry.item, itemPath);
            onItemSelect(itemPath, entry.labelStr, entry.item, labels);
        },
        [
            parentPath,
            hasChildrenFn,
            children,
            allowParentSelect,
            drillInto,
            navigationStack,
            registerLabel,
            registerItemForPath,
            onSelect,
            onItemSelect,
        ]
    );

    // Register item actions for keyboard navigation.
    // "select" always maps to row selection; "drill" navigates into children when possible.
    useEffect(() => {
        if (!isCurrentLevel) return;

        // Clear previous actions when this level becomes active
        clearItemActions();

        // Register actions for each item
        entries.forEach((entry, index) => {
            const itemHasChildren = hasChildrenFn?.(entry.item) ?? false;
            const canNavigate = itemHasChildren && children;

            registerItemAction(
                index,
                () => {
                    handleItemAction(entry);
                },
                "select"
            );

            if (canNavigate) {
                registerItemAction(
                    index,
                    () => {
                        drillInto(entry);
                    },
                    "drill"
                );
            }
        });

        return () => {
            clearItemActions();
        };
    }, [
        isCurrentLevel,
        entries,
        children,
        hasChildrenFn,
        drillInto,
        handleItemAction,
        registerItemAction,
        clearItemActions,
    ]);

    // Handle mouse enter for active state
    const handleMouseEnter = (index: number) => {
        setActiveIndex(index);
    };

    // Scroll active item into view when activeIndex changes
    useEffect(() => {
        if (!isCurrentLevel) return;

        const activeElement = itemRefs.current.get(activeIndex);
        if (activeElement) {
            activeElement.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
            });
        }
    }, [activeIndex, isCurrentLevel]);

    // Check if a path is selected
    const isPathSelected = useCallback(
        (itemPath: Path): boolean => {
            return selections.some((s) => pathsEqual(s.path, itemPath));
        },
        [selections]
    );

    // Don't render if this level is not the current navigation level
    if (!isCurrentLevel) {
        // But we still need to render children for the navigated item
        if (depth < navigationStack.length && children) {
            const navigatedItem = navigationStack[depth];
            // Find the item that was navigated into
            const parentItem = levelOptions?.find((item) => getKey(item) === navigatedItem.key);
            if (parentItem) {
                return (
                    <LevelContext.Provider
                        value={{ depth: depth + 1, parentPath: [...parentPath, navigatedItem.key] }}
                    >
                        {children(parentItem)}
                    </LevelContext.Provider>
                );
            }
        }
        return null;
    }

    if (!entries.length) {
        return null;
    }

    return (
        <List dense sx={{ padding: 1, margin: 0 }}>
            {entries.map((entry, index) => {
                const { item, label } = entry;
                const code = getCode?.(item);
                // Only show chevron/hasChildren if hasChildrenFn explicitly returns true
                const itemHasChildren = hasChildrenFn?.(item) ?? false;
                const canNavigate = itemHasChildren && children;
                const isActive = index === activeIndex;
                const itemPath: Path = [...parentPath, ...entry.relativePath];
                const isSelected = isPathSelected(itemPath);
                const ancestorLabels = entry.ancestors.map((ancestor) => ancestor.label);

                // Default element for the item
                const checkElement = isSelected ? (
                    <Check size={20} className="text-purple-500" />
                ) : null;
                const chevronElement =
                    canNavigate && (!isSelected || allowParentSelect) ? (
                        allowParentSelect ? (
                            <button
                                type="button"
                                className="inline-flex items-center justify-center w-6 h-6 rounded border border-transparent text-accent hover:bg-[#F9FAFB]"
                                onClick={(event) => {
                                    // Prevent row selection when using the chevron to navigate deeper.
                                    event.stopPropagation();
                                    drillInto(entry);
                                }}
                            >
                                <ChevronRight size={18} />
                            </button>
                        ) : (
                            <ChevronRight size={20} className="text-accent" />
                        )
                    ) : null;
                const defaultElement = (
                    <div className="flex flex-row items-center justify-between w-full">
                        <div className="text-accent">
                            {ancestorLabels.length > 0 && (
                                <span className="text-gray-500">
                                    {ancestorLabels.join(" › ")} ›{" "}
                                </span>
                            )}
                            {label}
                        </div>
                        <div className="flex items-center gap-2">
                            {code !== undefined && code !== null && code !== "" && (
                                <span className=" text-lightGreen-500 font-thin">{code}</span>
                            )}
                            {checkElement}
                            {chevronElement}
                        </div>
                    </div>
                );

                // Render props for custom rendering
                const itemRenderProps: ItemRenderProps = {
                    isActive,
                    isSelected,
                    isLoading: false,
                    hasChildren: itemHasChildren,
                    depth,
                    defaultElement,
                    checkElement,
                    chevronElement,
                    ancestorLabels,
                };

                return (
                    <ListItemButton
                        key={pathToKey(entry.relativePath)}
                        ref={(el) => {
                            if (el) {
                                itemRefs.current.set(index, el);
                            } else {
                                itemRefs.current.delete(index);
                            }
                        }}
                        selected={isActive}
                        onMouseEnter={() => handleMouseEnter(index)}
                        onClick={() => handleItemAction(entry)}
                        sx={{
                            borderRadius: "8px",
                            mb: 0.5,
                            py: 1,
                            px: 1.5,
                            transition: "none",
                            "&.Mui-selected": {
                                backgroundColor: "action.hover",
                            },
                            "&:hover": {
                                backgroundColor: "action.hover",
                            },
                        }}
                    >
                        {renderItem ? renderItem(item, itemRenderProps) : defaultElement}
                    </ListItemButton>
                );
            })}
        </List>
    );
}

export default Level;
